import React, { useEffect, useRef, useState } from "react";
import "./horizonTable.css";
import { toast } from "react-toastify";
import "../forms/themeCheckbox.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const THEME = "#8b1634";
const PLAN_ALERT_OPTIONS = [
  {
    left: { value: "Extremely Alert", label: "Extremely Alert" },
    right: { value: "Some signs of Sleepiness", label: "Some signs of Sleepiness" },
  },
  {
    left: { value: "Very Alert", label: "Very Alert" },
    right: { value: "Sleepy but no efforts to keep awake", label: "Sleepy but no efforts to keep awake" },
  },
  {
    left: { value: "Alert", label: "Alert" },
    right: { value: "Sleepy, Some effort to keep awake", label: "Sleepy, Some effort to keep awake" },
  },
  {
    left: { value: "Rather Alert", label: "Rather Alert" },
    right: { value: "Very sleepy, great effort keeping awake", label: "Very sleepy, great effort keeping awake" },
  },
  {
    left: { value: "Neither Alert nor Sleepy", label: "Neither Alert nor Sleepy" },
    right: null,
  },
];

function getBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL;
  return apiUrl?.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl || "";
}

function formatDate(d) {
  if (!d) return "—";
  const date = new Date(d);
  return isNaN(date.getTime()) ? "—" : date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function getFormName(level) {
  if (level === "General") {
    return "SF-008-A";
  }
  if (level === "Level 1") {
    return "SF-008-B";
  }
  if (level === "Level 2") {
    return "SF-008-C";
  }
  if (level === "Plan") {
    return "SF-008-D";
  }
  return level || "—";
}

function getApiPathForLevel(level) {
  if (level === "General") return "a-general";
  if (level === "Level 1") return "b-level1";
  if (level === "Level 2") return "c-level2";
  if (level === "Plan") return "d-fatigue-management-plan";
  return "a-general";
}

function getDateInputValue(d) {
  if (!d) return "";
  const iso = new Date(d);
  if (!isNaN(iso.getTime())) {
    return iso.toISOString().slice(0, 10);
  }
  if (typeof d === "string" && d.includes("/")) {
    const [dd, mm, yyyy] = d.split("/");
    if (dd && mm && yyyy) {
      return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
    }
  }
  if (typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
    return d;
  }
  return "";
}

function formatPersonName(person) {
  if (!person || typeof person !== "object") return "";
  const fullName = [person.firstName, person.lastName].filter(Boolean).join(" ").trim();
  return fullName || person.fullName || person.name || person.email || "";
}

function isPlaceholderName(name) {
  const s = String(name || "").trim();
  return !s || s === "—" || s === "---" || s === "-";
}

function getWorksheetWorkerName(item) {
  if (!Array.isArray(item?.workSheetId?.workerIds) || item.workSheetId.workerIds.length === 0) return "";
  return item.workSheetId.workerIds
    .map((w) => (w && typeof w === "object" ? [w.firstName, w.lastName].filter(Boolean).join(" ").trim() || w.email || "" : ""))
    .filter(Boolean)
    .join(", ");
}

function getFormSiteName(item) {
  return (
    item?.workSheetId?.siteId?.siteName ||
    item?.workSheetId?.name ||
    "—"
  );
}

function getCreatedByName(item, fallbackWorkerName) {
  const managerName = formatPersonName(item?.createdBy);
  if (managerName && !isPlaceholderName(managerName)) return managerName;
  const fallback = fallbackWorkerName && !isPlaceholderName(fallbackWorkerName) ? fallbackWorkerName : "";
  const workerName =
    formatPersonName(item?.appUserId) ||
    formatPersonName(item?.userId) ||
    fallback;
  if (workerName && !isPlaceholderName(workerName)) return workerName;
  return "—";
}

function getFormWorkerName(item) {
  const fromUser = formatPersonName(item?.appUserId) || formatPersonName(item?.userId);
  if (fromUser && !isPlaceholderName(fromUser)) return fromUser;
  const fromSheet = getWorksheetWorkerName(item);
  if (fromSheet && !isPlaceholderName(fromSheet)) return fromSheet;
  return "—";
}

function FatigueExceedanceFormsPage() {
  const [forms, setForms] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [formNameFilter, setFormNameFilter] = useState("");
  const [workerFilter, setWorkerFilter] = useState("");
  const [workerOptions, setWorkerOptions] = useState([]);
  const [workerDropdownOpen, setWorkerDropdownOpen] = useState(false);
  const [workerSearch, setWorkerSearch] = useState("");
  const workerDropdownRef = useRef(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editPayload, setEditPayload] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteForm, setDeleteForm] = useState(null);

  useEffect(() => {
    fetchForms();
    fetchPendingCount();
  }, [statusFilter, formNameFilter, workerFilter]);

  useEffect(() => {
    if (!workerDropdownOpen) return;
    function handleClickOutside(e) {
      if (workerDropdownRef.current && !workerDropdownRef.current.contains(e.target)) setWorkerDropdownOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [workerDropdownOpen]);

  async function fetchForms() {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const [resA, resB, resC, resD] = await Promise.all([
        fetch(getBaseUrl() + "/api/a-general", { headers }),
        fetch(getBaseUrl() + "/api/b-level1", { headers }),
        fetch(getBaseUrl() + "/api/c-level2", { headers }),
        fetch(getBaseUrl() + "/api/d-fatigue-management-plan", { headers }),
      ]);

      const safeJson = async (res) => {
        try {
          return await res.json();
        } catch {
          return null;
        }
      };

      const [dataA, dataB, dataC, dataD] = await Promise.all([
        safeJson(resA),
        safeJson(resB),
        safeJson(resC),
        safeJson(resD),
      ]);

      const listA = dataA && dataA.success && Array.isArray(dataA.data) ? dataA.data : [];
      const listB = dataB && dataB.success && Array.isArray(dataB.data) ? dataB.data : [];
      const listC = dataC && dataC.success && Array.isArray(dataC.data) ? dataC.data : [];
      const listD = dataD && dataD.success && Array.isArray(dataD.data) ? dataD.data : [];

      const mapped = [
        ...listA.map((item) => ({
          ...item,
          _id: item._id,
          level: "General",
          formName: item.formName || getFormName("General"),
          status: item.status || "pending",
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
          workerName: (Array.isArray(item.workSheetId?.workerIds) && item.workSheetId.workerIds.length > 0
            ? item.workSheetId.workerIds.map((w) => (w && typeof w === "object" ? [w.firstName, w.lastName].filter(Boolean).join(" ").trim() || w.email || "" : "")).filter(Boolean).join(", ")
            : "—"),
          filledBy: getCreatedByName(
            item,
            Array.isArray(item.workSheetId?.workerIds) && item.workSheetId.workerIds.length > 0
              ? item.workSheetId.workerIds
                  .map((w) => (w && typeof w === "object" ? [w.firstName, w.lastName].filter(Boolean).join(" ").trim() || w.email || "" : ""))
                  .filter(Boolean)
                  .join(", ")
              : "—"
          ),
          siteName:
            item.workSheetId?.siteId?.siteName ||
            item.workSheetId?.name ||
            "—",
        })),
        ...listB.map((item) => ({
          ...item,
          _id: item._id,
          level: "Level 1",
          formName: item.formName || getFormName("Level 1"),
          status: item.status || "pending",
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
          workerName: (Array.isArray(item.workSheetId?.workerIds) && item.workSheetId.workerIds.length > 0
            ? item.workSheetId.workerIds.map((w) => (w && typeof w === "object" ? [w.firstName, w.lastName].filter(Boolean).join(" ").trim() || w.email || "" : "")).filter(Boolean).join(", ")
            : "—"),
          filledBy: getCreatedByName(
            item,
            Array.isArray(item.workSheetId?.workerIds) && item.workSheetId.workerIds.length > 0
              ? item.workSheetId.workerIds
                  .map((w) => (w && typeof w === "object" ? [w.firstName, w.lastName].filter(Boolean).join(" ").trim() || w.email || "" : ""))
                  .filter(Boolean)
                  .join(", ")
              : "—"
          ),
          siteName:
            item.workSheetId?.siteId?.siteName ||
            item.workSheetId?.name ||
            "—",
        })),
        ...listC.map((item) => ({
          ...item,
          _id: item._id,
          level: "Level 2",
          formName: item.formName || getFormName("Level 2"),
          status: item.status || "pending",
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
          workerName: (Array.isArray(item.workSheetId?.workerIds) && item.workSheetId.workerIds.length > 0
            ? item.workSheetId.workerIds.map((w) => (w && typeof w === "object" ? [w.firstName, w.lastName].filter(Boolean).join(" ").trim() || w.email || "" : "")).filter(Boolean).join(", ")
            : "—"),
          filledBy: getCreatedByName(
            item,
            Array.isArray(item.workSheetId?.workerIds) && item.workSheetId.workerIds.length > 0
              ? item.workSheetId.workerIds
                  .map((w) => (w && typeof w === "object" ? [w.firstName, w.lastName].filter(Boolean).join(" ").trim() || w.email || "" : ""))
                  .filter(Boolean)
                  .join(", ")
              : "—"
          ),
          siteName:
            item.workSheetId?.siteId?.siteName ||
            item.workSheetId?.name ||
            "—",
        })),
        ...listD.map((item) => ({
          ...item,
          _id: item._id,
          level: "Plan",
          formName: item.formName || getFormName("Plan"),
          status: item.status || "pending",
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
          workerName:
            item.userId && typeof item.userId === "object"
              ? [item.userId.firstName, item.userId.lastName].filter(Boolean).join(" ").trim() || item.userId.email || "—"
              : "—",
          filledBy: getCreatedByName(
            item,
            item.userId && typeof item.userId === "object"
              ? [item.userId.firstName, item.userId.lastName].filter(Boolean).join(" ").trim() || item.userId.email || "—"
              : "—"
          ),
          siteName: "—",
        })),
      ];

      const normalizedMapped = mapped.map((f) => {
        if (f.level === "Plan") return f;
        const workerName = getFormWorkerName(f);
        return {
          ...f,
          workerName,
          siteName: getFormSiteName(f),
          filledBy: getCreatedByName(f, workerName),
        };
      });

      const uniqueWorkers = Array.from(
        new Set(
          normalizedMapped
            .map((f) => f.workerName)
            .filter((name) => name && name !== "—")
        )
      ).sort((a, b) => a.localeCompare(b));
      setWorkerOptions(uniqueWorkers);

      const filtered = normalizedMapped.filter((f) => {
        if (statusFilter && statusForFilter(f.status) !== statusFilter) return false;
        if (formNameFilter && f.level !== formNameFilter) return false;
        if (workerFilter && !(f.workerName || "").toLowerCase().includes(workerFilter.toLowerCase())) return false;
        return true;
      });

      const statusRank = (s) => {
        const k = statusForFilter(s);
        if (k === "pending") return 0;
        if (k === "approved") return 1;
        if (k === "rejected") return 2;
        return 3;
      };
      const sorted = [...filtered].sort((a, b) => {
        const ra = statusRank(a?.status);
        const rb = statusRank(b?.status);
        if (ra !== rb) return ra - rb;
        const ta = new Date(a?.updatedAt || a?.createdAt || 0).getTime();
        const tb = new Date(b?.updatedAt || b?.createdAt || 0).getTime();
        return tb - ta;
      });

      setForms(sorted);
      setError(null);
    } catch {
      setForms([]);
      setError("Network error");
    } finally {
      setLoading(false);
    }
  }

  async function fetchPendingCount() {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/fatigue-exceedance-forms/pending-count", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setPendingCount(data.data?.count ?? 0);
      }
    } catch (e) {
      console.error(e);
    }
  }

  function buildGeneralPayloadFromForm(f) {
    const normalizeKeyValueToObject = (input) => {
      if (!input) return {};
      if (Array.isArray(input)) {
        return input.reduce((acc, item) => {
          if (!item) return acc;
          const k = item.key != null ? String(item.key) : "";
          if (!k) return acc;
          acc[k] = item.value != null ? String(item.value) : "";
          return acc;
        }, {});
      }
      if (typeof input === "object") return input;
      return {};
    };
    const normalizeRequiredBlock = (block) => {
      if (!block || typeof block !== "object") return {};
      const requiredRaw = block.required;
      let required = requiredRaw;
      if (typeof requiredRaw === "string") {
        const s = requiredRaw.trim().toLowerCase();
        if (s === "yes" || s === "true") required = true;
        else if (s === "no" || s === "false") required = false;
      }
      return { ...block, required };
    };
    const normalizeTime = (val) => {
      if (val == null || val === "") return "";
      const s = String(val).trim();
      const hm = s.match(/^(\d{1,2}):(\d{2})/);
      if (hm) return `${String(hm[1]).padStart(2, "0")}:${hm[2]}`;
      if (/^\d{4}-\d{2}-\d{2}T/.test(s)) {
        const d = new Date(s);
        if (!Number.isNaN(d.getTime())) return `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
      }
      const d = new Date(s);
      if (!Number.isNaN(d.getTime())) return `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
      return "";
    };
    return {
      workSheetId: f.workSheetId?._id || f.workSheetId || "",
      id: f.id || "",
      managerComment: f.managerComment || "",
      jobLocation: f.jobLocation || "",
      jobNo: f.jobNo || "",
      operatorName: f.operatorName || "",
      date: f.date || "",
      clientName: f.clientName || "",
      exceedanceTypes: normalizeKeyValueToObject(f.exceedanceTypes),
      proposedExceedanceFrom: normalizeTime(f.proposedExceedanceFrom),
      proposedExceedanceTo: normalizeTime(f.proposedExceedanceTo),
      actualExceedanceFrom: normalizeTime(f.actualExceedanceFrom),
      actualExceedanceTo: normalizeTime(f.actualExceedanceTo),
      proposedExceedanceHours: f.proposedExceedanceHours ?? null,
      proposedExceedanceMinutes: f.proposedExceedanceMinutes ?? null,
      actualExceedanceHours: f.actualExceedanceHours ?? null,
      actualExceedanceMinutes: f.actualExceedanceMinutes ?? null,
      ItemsAssessingProposedExceedance: normalizeKeyValueToObject(f.ItemsAssessingProposedExceedance),
      reasonForExceedanceComments: f.reasonForExceedanceComments || "",
      revisedFRIScoresFollowingMitigation: f.revisedFRIScoresFollowingMitigation || "",
      fatigueManagementPlanRequired: normalizeRequiredBlock(f.fatigueManagementPlanRequired),
      fatigueManagementPlanReviewRequired: normalizeRequiredBlock(f.fatigueManagementPlanReviewRequired),
      exceedanceApprovedBy: f.exceedanceApprovedBy || {}
    };
  }

  function toHHmm(val) {
    if (val == null || val === "") return "";
    const s = String(val).trim();
    if (/^\d{1,2}:\d{2}:$/.test(s)) return s.slice(0, -1);
    const hm = s.match(/^(\d{1,2}):(\d{2})/);
    if (hm) return `${String(hm[1]).padStart(2, "0")}:${hm[2]}`;
    if (/^\d{4}-\d{2}-\d{2}T/.test(s)) {
      const d = new Date(s);
      if (!Number.isNaN(d.getTime())) return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    }
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    return "";
  }

  function openEditModal(form) {
    const payload =
      form.level === "General"
        ? buildGeneralPayloadFromForm(form)
        : form.level === "Plan"
        ? {
            id: form.id || "",
            userId: form.userId?._id || form.userId || "",
            managerComment: form.managerComment || "",
            employeeName: form.employeeName || "",
            employeeNumber: form.employeeNumber || "",
            sentinelNumber: form.sentinelNumber || "",
            normalWorkLocation: form.normalWorkLocation || "",
            jobRole: form.jobRole || "",
            date: getDateInputValue(form.date),
            managerNameAndRole: form.managerNameAndRole || "",
            reasonForCreation: form.reasonForCreation || "",
            howAlertAreYouFeeling: form.howAlertAreYouFeeling || "",
            workStatusOverPlan: form.workStatusOverPlan || "",
            existingWorkPatterns: form.existingWorkPatterns || {},
            reviewOfControlMeasures: form.reviewOfControlMeasures || {},
            medicalReviewPlans: form.medicalReviewPlans || {},
            otherComments: form.otherComments || {},
            actionPlanAgreement: form.actionPlanAgreement || {
              employeeSignature: "",
              employeeDate: "",
              managerSignature: "",
              managerDate: "",
            },
          }
        : (() => {
            const copy = { ...form };
            delete copy._id;
            delete copy.level;
            delete copy.formName;
            delete copy.workerName;
            delete copy.siteName;

            Object.entries(copy).forEach(([key, value]) => {
              if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
                copy[key] = getDateInputValue(value);
              }
            });

            if (copy.exceedanceApprovedBy && typeof copy.exceedanceApprovedBy === "object") {
              const ea = { ...copy.exceedanceApprovedBy };
              if (ea.date) {
                ea.date = getDateInputValue(ea.date);
              }
              copy.exceedanceApprovedBy = ea;
            }

            return copy;
          })();
    setEditForm(form);
    setEditPayload(payload);
    setShowEditModal(true);
  }

  function openDeleteModal(form) {
    setDeleteForm(form);
    setShowDeleteModal(true);
  }

  async function handleSaveEdit() {
    if (!editForm) return;
    let body = editPayload;
    if (!body || typeof body !== "object") {
      toast.error("Nothing to save. Please update the form data.");
      return;
    }
    if (editForm.level === "General") {
    if (!body.workSheetId || !body.id) {
      toast.error("Both workSheetId and id are required in the data.");
      return;
      }
    }
    if (editForm.level === "Plan") {
      body = {
        id: editPayload.id || "",
        userId:
          editPayload.userId ||
          editForm.userId?._id ||
          editForm.userId ||
          "",
        managerComment: editPayload.managerComment || "",
        employeeName: editPayload.employeeName || "",
        employeeNumber: editPayload.employeeNumber || "",
        sentinelNumber: editPayload.sentinelNumber || "",
        normalWorkLocation: editPayload.normalWorkLocation || "",
        jobRole: editPayload.jobRole || "",
        date: editPayload.date || "",
        managerNameAndRole: editPayload.managerNameAndRole || "",
        reasonForCreation: editPayload.reasonForCreation || "",
        howAlertAreYouFeeling: editPayload.howAlertAreYouFeeling || "",
        workStatusOverPlan: editPayload.workStatusOverPlan || "",
        existingWorkPatterns: editPayload.existingWorkPatterns || {},
        reviewOfControlMeasures: editPayload.reviewOfControlMeasures || {},
        medicalReviewPlans: editPayload.medicalReviewPlans || {},
        otherComments: editPayload.otherComments || {},
        actionPlanAgreement: editPayload.actionPlanAgreement || {
          employeeSignature: "",
          employeeDate: "",
          managerSignature: "",
          managerDate: "",
        },
      };
    }
    setEditSaving(true);
    try {
      const token = localStorage.getItem("token");
      const path = getApiPathForLevel(editForm.level);
      const res = await fetch(getBaseUrl() + "/api/" + path + "/" + editForm._id, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success("Exceedance form updated.");
        setShowEditModal(false);
        setEditForm(null);
        fetchForms();
      } else {
        toast.error(data.message || "Failed to update exceedance form.");
      }
    } catch {
      toast.error("Failed to update exceedance form.");
    } finally {
      setEditSaving(false);
    }
  }

  async function handleConfirmDelete() {
    if (!deleteForm) return;
    try {
      const token = localStorage.getItem("token");
      const path = getApiPathForLevel(deleteForm.level);
      const res = await fetch(getBaseUrl() + "/api/" + path + "/" + deleteForm._id, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success("Exceedance form deleted.");
        setShowDeleteModal(false);
        setDeleteForm(null);
        fetchForms();
      } else {
        toast.error(data.message || "Failed to delete exceedance form.");
      }
    } catch {
      toast.error("Failed to delete exceedance form.");
    }
  }

  async function handleStatusChange(f, newStatus) {
    if (!newStatus || newStatus === statusForFilter(f.status)) return;
    const currentStatus = statusForFilter(f.status);
    if (currentStatus !== "pending" && newStatus === "pending") {
      toast.error("Status cannot be changed back to Pending.");
      return;
    }
    setStatusUpdatingId(f._id);
    try {
      const token = localStorage.getItem("token");
      const base = getBaseUrl();
      const path =
        f.level === "General"
          ? "a-general"
          : f.level === "Level 1"
          ? "b-level1"
          : f.level === "Level 2"
          ? "c-level2"
          : "d-fatigue-management-plan";
      const res = await fetch(`${base}/api/${path}/${f._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        if ((newStatus === "approved" || newStatus === "rejected") && f.level !== "Plan") {
          const worksheetId = f.workSheetId?._id || f.workSheetId || "";
          const entryId = f.id || "";
          if (worksheetId && entryId) {
            try {
              await fetch(`${base}/api/shift-entry-update-data`, {
                method: "PUT",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                  id: entryId,
                  worksheet_Id: worksheetId,
                  isManagerActionTaken: true,
                }),
              });
            } catch {}
          }
        }
        toast.success("Status updated.");
        fetchForms();
        window.dispatchEvent(new Event("exceedanceFormsUpdated"));
      } else {
        toast.error(data.message || "Failed to update status");
      }
    } catch {
      toast.error("Failed to update status");
    } finally {
      setStatusUpdatingId(null);
    }
  }

  const statusLabel = (s) => {
    const map = {
      pending: "Pending",
      approved: "Approved",
      rejected: "Rejected",
      pending_employee: "Pending",
      employee_submitted: "Pending",
      pending_manager_review: "Pending",
      manager_approved: "Approved",
      manager_rejected: "Rejected",
    };
    return map[s] || s;
  };
  const statusForFilter = (s) => {
    if (s === "approved" || s === "manager_approved") return "approved";
    if (s === "rejected" || s === "manager_rejected") return "rejected";
    return "pending";
  };

  return (
    <div
      style={{
        background: "#ffffff",
        borderRadius: "20px",
        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
        padding: "24px",
        marginBottom: "24px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "24px",
          flexWrap: "wrap",
          gap: "16px",
        }}
      >
        <h2 style={{ fontSize: "24px", fontWeight: "700", color: "#1a202c", margin: 0 }}>
          Exceedance Forms
          {pendingCount > 0 && (
            <span
              style={{
                marginLeft: "12px",
                background: "#e53e3e",
                color: "#fff",
                padding: "4px 10px",
                borderRadius: "8px",
                fontSize: "14px",
              }}
            >
              {pendingCount} pending
            </span>
          )}
        </h2>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "12px",
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          <select
            value={formNameFilter}
            onChange={(e) => setFormNameFilter(e.target.value)}
            style={{
              padding: "8px 12px",
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
              fontSize: "14px",
              minWidth: "200px",
            }}
          >
            <option value="">All forms</option>
            <option value="General">SF-008-A</option>
            <option value="Level 1">SF-008-B</option>
            <option value="Level 2">SF-008-C</option>
            <option value="Plan">SF-008-D</option>
          </select>
          <div ref={workerDropdownRef} style={{ position: "relative", minWidth: "220px" }}>
            <input
              type="text"
              value={workerSearch}
              onFocus={() => setWorkerDropdownOpen(true)}
              onChange={(e) => {
                const val = e.target.value;
                setWorkerSearch(val);
                setWorkerFilter(val);
                setWorkerDropdownOpen(true);
              }}
              placeholder="Search worker"
              style={{
                padding: "8px 12px",
                borderRadius: "8px",
                border: workerDropdownOpen ? "2px solid #8b1634" : "1px solid #e2e8f0",
                fontSize: "14px",
                minWidth: "220px",
                outline: "none",
                boxShadow: workerDropdownOpen ? "0 0 0 1px rgba(139, 22, 52, 0.1)" : "none",
              }}
            />
            {(workerFilter || workerSearch) && (
              <button
                type="button"
                onClick={() => {
                  setWorkerFilter("");
                  setWorkerSearch("");
                }}
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  padding: 0,
                  color: "#4a5568",
                  fontSize: "14px",
                }}
              >
                ×
              </button>
            )}
            {workerDropdownOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  marginTop: "4px",
                  background: "#ffffff",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 10px 30px rgba(15, 23, 42, 0.18)",
                  maxHeight: "260px",
                  overflowY: "auto",
                  zIndex: 20,
                }}
                onMouseDown={(e) => e.preventDefault()}
              >
                {workerOptions
                  .filter((name) =>
                    !workerSearch
                      ? true
                      : name.toLowerCase().includes(workerSearch.toLowerCase())
                  )
                  .map((name) => (
                    <div
                      key={name}
                      onClick={() => {
                        setWorkerFilter(name);
                        setWorkerSearch(name);
                        setWorkerDropdownOpen(false);
                      }}
                      style={{
                        padding: "8px 12px",
                        fontSize: "14px",
                        cursor: "pointer",
                        color: "#1a202c",
                        borderBottom: "1px solid #f1f5f9",
                        background:
                          workerFilter === name ? "rgba(139, 22, 52, 0.08)" : "#ffffff",
                      }}
                    >
                      {name}
                    </div>
                  ))}
                {workerOptions.length === 0 && (
                  <div
                    style={{
                      padding: "8px 12px",
                      fontSize: "13px",
                      color: "#a0aec0",
                    }}
                  >
                    No workers found
                  </div>
                )}
              </div>
            )}
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: "8px 12px",
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
              fontSize: "14px",
              minWidth: "150px",
            }}
          >
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          {(statusFilter || formNameFilter || workerFilter) && (
            <button
              type="button"
              onClick={() => {
                setStatusFilter("");
                setFormNameFilter("");
                setWorkerFilter("");
                setWorkerSearch("");
                setWorkerDropdownOpen(false);
              }}
              style={{
                padding: "8px 14px",
                borderRadius: "8px",
                border: "none",
                background: "#e53e3e",
                fontSize: "13px",
                fontWeight: 500,
                color: "#fff",
                cursor: "pointer",
              }}
            >
              Reset
            </button>
          )}
        </div>
      </div>

      <div style={{ overflowX: "auto", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1.2fr 1.4fr 1.1fr 1.1fr 1.5fr 0.8fr", background: "#f7fafc", padding: "12px 16px", borderBottom: "1px solid #e2e8f0", gap: "12px", alignItems: "center" }}>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Form name</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Worker</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Site</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Status</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Updated Date</div>
          <div style={{ fontWeight: "700", color: "#8b1634", fontSize: "14px" }}>Created By</div>
          <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Actions</div>
        </div>
        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#8b1634", fontWeight: "600" }}>Loading...</div>
        ) : error ? (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#e53e3e" }}>{error}</div>
        ) : forms.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#718096" }}>
            No exceedance forms. When a fatigue rule is broken, a form is created and the employee is notified to fill it.
          </div>
        ) : (
          forms.map((f, idx) => (
            <div
              key={f._id || idx}
              style={{
                display: "grid",
                gridTemplateColumns: "1.6fr 1.2fr 1.4fr 1.1fr 1.1fr 1.5fr 0.8fr",
                padding: "16px",
                borderBottom: "1px solid #f1f5f9",
                gap: "12px",
                alignItems: "center",
              }}
            >
              <div style={{ color: "#2d3748", fontSize: "14px" }}>{getFormName(f.level)}</div>
              <div style={{ color: "#2d3748", fontSize: "14px" }}>{f.workerName || "—"}</div>
              <div style={{ color: "#2d3748", fontSize: "14px" }}>{f.siteName || "—"}</div>
              <div style={{ display: "flex", alignItems: "center" }}>
                <select
                  value={statusForFilter(f.status)}
                  onChange={(e) => handleStatusChange(f, e.target.value)}
                  disabled={statusUpdatingId === f._id}
                  style={{
                    padding: "6px 10px",
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                    fontSize: "13px",
                    minWidth: "130px",
                    background: "#ffffff",
                    color: "#2d3748",
                  }}
                >
                  {statusForFilter(f.status) === "pending" && <option value="pending">Pending</option>}
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <div style={{ color: "#2d3748", fontSize: "14px" }}>{formatDate(f.updatedAt || f.createdAt)}</div>
              <div style={{ color: "#1f2937", fontSize: "14px", fontWeight: 600 }}>
                {f.filledBy || "—"}
              </div>
              <div style={{ display: "flex", gap: "8px", alignItems: "center", justifyContent: "flex-start" }}>
                <button
                  type="button"
                  onClick={() => openEditModal(f)}
                  title="Edit"
                  style={{
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    padding: "6px",
                    borderRadius: "6px",
                    transition: "background-color 0.2s",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f0fdf4")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                >
                  <i className="fa fa-edit" style={{ color: "#22c55e", fontSize: "16px" }}></i>
                </button>
              </div>
            </div>
          ))
        )}
      </div>
      {showEditModal && editPayload && (
        <div
          className="modal fade show"
          style={{ display: "block", background: "rgba(0,0,0,0.3)" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div
              className="modal-content"
              style={{
                maxWidth: "1200px",
                margin: "0 auto",
                borderRadius: "16px",
                boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
              }}
            >
              <div
                className="modal-header"
                style={{
                  borderBottom: "2px solid #f0f0f0",
                  padding: "16px 20px",
                  background: "linear-gradient(135deg, #8b1634 0%, #a01a3f 100%)",
                }}
              >
                <h5 className="modal-title" style={{ color: "#fff", fontWeight: 600, fontSize: "18px" }}>
                  Edit Exceedance Assessment
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowEditModal(false)}
                  style={{ opacity: 0.9 }}
                />
              </div>
              <div className="modal-body" style={{ padding: "20px" }}>
                {editForm && editForm.level === "General" && (
                  <div style={{ marginBottom: "20px" }}>
                    <p style={{ fontSize: "13px", color: "#4a5568", marginBottom: "12px" }}>
                      <strong>Note:</strong> This assessment is to be completed by the Line Manager or On-Call Manager
                      prior to the proposed exceedance of the planned working hours due to exceptional circumstances
                      or unplanned works.
                    </p>
                  </div>
                )}
                {editForm && editForm.level === "Level 1" && (
                  <div style={{ marginBottom: "20px" }}>
                    <p style={{ fontSize: "13px", color: "#4a5568", marginBottom: "8px" }}>
                      <strong>Notes:</strong> This assessment is to be completed by the Line Manager or On-Call Manager
                      prior to the proposed exceedance of the planned working hours due to exceptional circumstances
                      or unplanned works.
                    </p>
                    <p style={{ fontSize: "13px", color: "#4a5568", marginBottom: "8px" }}>
                      A Level 1 exceedance is triggered when an individual works 60 or more hours but less than 72 hours
                      in any rolling 7-day period.
                    </p>
                    <p style={{ fontSize: "13px", color: "#4a5568", marginBottom: 0 }}>
                      This form may also be used where a daytime FRI score exceeds 35, a night time FRI score exceeds 45,
                      or a risk score exceeds 1.6, where rosters are worked.
                    </p>
                  </div>
                )}
                <div className="row">
                  <div className="mb-3 col-md-12">
                    <label className="form-label">Manager Comment</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      value={editPayload.managerComment || ""}
                      onChange={(e) => setEditPayload((prev) => ({ ...prev, managerComment: e.target.value }))}
                      placeholder="Enter manager comment"
                    />
                  </div>
                </div>
                <div className="row">
                  {editForm && editForm.level === "Plan" && (
                    <>
                      <div className="mb-3 col-md-3">
                        <label className="form-label">Employee name</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editPayload.employeeName || ""}
                          onChange={(e) => setEditPayload((prev) => ({ ...prev, employeeName: e.target.value }))}
                        />
                      </div>
                      <div className="mb-3 col-md-3">
                        <label className="form-label">Employee number</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editPayload.employeeNumber || ""}
                          onChange={(e) => setEditPayload((prev) => ({ ...prev, employeeNumber: e.target.value }))}
                        />
                      </div>
                      <div className="mb-3 col-md-3">
                        <label className="form-label">Sentinel number (if applicable)</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editPayload.sentinelNumber || ""}
                          onChange={(e) => setEditPayload((prev) => ({ ...prev, sentinelNumber: e.target.value }))}
                        />
                      </div>
                      <div className="mb-3 col-md-3">
                        <label className="form-label">Normal Work location</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editPayload.normalWorkLocation || ""}
                          onChange={(e) => setEditPayload((prev) => ({ ...prev, normalWorkLocation: e.target.value }))}
                        />
                      </div>
                      <div className="mb-3 col-md-3">
                        <label className="form-label">Job role</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editPayload.jobRole || ""}
                          onChange={(e) => setEditPayload((prev) => ({ ...prev, jobRole: e.target.value }))}
                        />
                      </div>
                      <div className="mb-3 col-md-3">
                        <label className="form-label">Date</label>
                        <input
                          type="date"
                          className="form-control"
                          value={editPayload.date || ""}
                          onChange={(e) => setEditPayload((prev) => ({ ...prev, date: e.target.value }))}
                        />
                      </div>
                      <div className="mb-3 col-md-6">
                        <label className="form-label">
                          Name and role of person (or line manager) completing fatigue management plan
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={editPayload.managerNameAndRole || ""}
                          onChange={(e) =>
                            setEditPayload((prev) => ({ ...prev, managerNameAndRole: e.target.value }))
                          }
                        />
                      </div>
                      <div className="mb-3 col-md-12">
                        <label className="form-label">
                          Reason for creation of fatigue management plan (trigger type). Include detail on the (potential)
                          causes and impact of the fatigue issue identified:
                        </label>
                        <textarea
                          className="form-control"
                          rows={3}
                          value={editPayload.reasonForCreation || ""}
                          onChange={(e) =>
                            setEditPayload((prev) => ({ ...prev, reasonForCreation: e.target.value }))
                          }
                        />
                      </div>
                      <div className="mb-3 col-md-12">
                        <div className="fw-bold mb-2" style={{ color: "#8b1634" }}>
                          How alert are you feeling? (Select one)
                        </div>
                        <div className="table-responsive">
                          <table className="table table-bordered mb-0">
                            <tbody>
                              {PLAN_ALERT_OPTIONS.map((opt, index) => {
                                const current = String(editPayload.howAlertAreYouFeeling || "").trim();
                                const leftSelected = current === opt.left.value;
                                const rightSelected = opt.right && current === opt.right.value;
                                return (
                                  <tr key={opt.left.value || index}>
                                    <td style={{ width: "50%" }}>
                                      <div className="form-check">
                                        <input
                                          className="form-check-input theme-checkbox"
                                          type="radio"
                                          name="howAlertPlan"
                                          id={`plan-alert-left-${index}`}
                                          checked={leftSelected}
                                          onChange={() =>
                                            setEditPayload((prev) => ({
                                              ...prev,
                                              howAlertAreYouFeeling: opt.left.value,
                                            }))
                                          }
                                          style={{ accentColor: THEME }}
                                        />
                                        <label
                                          className="form-check-label"
                                          htmlFor={`plan-alert-left-${index}`}
                                          style={{ marginLeft: "4px" }}
                                        >
                                          {opt.left.label}
                                        </label>
                                      </div>
                                    </td>
                                    <td style={{ width: "50%" }}>
                                      {opt.right && (
                                        <div className="form-check">
                                          <input
                                            className="form-check-input theme-checkbox"
                                            type="radio"
                                            name="howAlertPlan"
                                            id={`plan-alert-right-${index}`}
                                            checked={rightSelected}
                                            onChange={() =>
                                              setEditPayload((prev) => ({
                                                ...prev,
                                                howAlertAreYouFeeling: opt.right.value,
                                              }))
                                            }
                                            style={{ accentColor: THEME }}
                                          />
                                          <label
                                            className="form-check-label"
                                            htmlFor={`plan-alert-right-${index}`}
                                            style={{ marginLeft: "4px" }}
                                          >
                                            {opt.right.label}
                                          </label>
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div className="mb-3 col-md-12">
                        <label className="form-label">
                          Work status over the course of the fatigue management plan (in work, restricted duties
                          recommended, working hours or duties limitations etc.)
                        </label>
                        <textarea
                          className="form-control"
                          rows={2}
                          value={editPayload.workStatusOverPlan || ""}
                          onChange={(e) =>
                            setEditPayload((prev) => ({ ...prev, workStatusOverPlan: e.target.value }))
                          }
                        />
                      </div>
                      <div className="mb-3 col-md-12">
                        <div className="table-responsive" style={{ borderRadius: "10px", overflow: "hidden", boxShadow: "0 4px 12px rgba(15,23,42,0.08)" }}>
                          <table className="table mb-0" style={{ marginBottom: 0 }}>
                            <thead>
                              <tr>
                                <th
                                  style={{
                                    width: "45%",
                                    background: "#f9fafb",
                                    borderBottom: "1px solid #e5e7eb",
                                    fontWeight: 600,
                                    fontSize: "13px",
                                    color: "#4b5563",
                                  }}
                                >
                                  Risk area and discussion points
                                </th>
                                <th
                                  style={{
                                    width: "40%",
                                    background: "#f9fafb",
                                    borderBottom: "1px solid #e5e7eb",
                                    fontWeight: 600,
                                    fontSize: "13px",
                                    color: "#4b5563",
                                  }}
                                >
                                  Agreed action (including accountabilities and responsibilities)
                                </th>
                                <th
                                  style={{
                                    width: "15%",
                                    background: "#f9fafb",
                                    borderBottom: "1px solid #e5e7eb",
                                    fontWeight: 600,
                                    fontSize: "13px",
                                    color: "#4b5563",
                                    textAlign: "center",
                                  }}
                                >
                                  Review date
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td style={{ verticalAlign: "top", padding: "12px 14px" }}>
                                  <div className="fw-bold mb-1" style={{ color: "#8b1634", fontSize: "13px" }}>
                                    Existing work patterns
                                  </div>
                                  <p style={{ fontSize: "12px", marginBottom: 0, color: "#4b5563" }}>
                                    • Discuss working hours and patterns leading to fatigue management plan.
                                    <br />
                                    • Identify any health related or family/home issues that may be considered.
                                  </p>
                                </td>
                                <td style={{ verticalAlign: "top", padding: "12px 14px" }}>
                                  <textarea
                                    className="form-control"
                                    rows={3}
                                    value={editPayload.existingWorkPatterns?.agreedActions || ""}
                                    onChange={(e) =>
                                      setEditPayload((prev) => ({
                                        ...prev,
                                        existingWorkPatterns: {
                                          ...(prev.existingWorkPatterns || {}),
                                          agreedActions: e.target.value,
                                        },
                                      }))
                                    }
                                  />
                                </td>
                                <td style={{ verticalAlign: "top", padding: "12px 10px", textAlign: "center" }}>
                                  <DatePicker
                                    selected={
                                      editPayload.existingWorkPatterns?.reviewPeriodDate
                                        ? new Date(getDateInputValue(editPayload.existingWorkPatterns.reviewPeriodDate))
                                        : null
                                    }
                                    onChange={(d) =>
                                      setEditPayload((prev) => ({
                                        ...prev,
                                        existingWorkPatterns: {
                                          ...(prev.existingWorkPatterns || {}),
                                          reviewPeriodDate: d ? d.toISOString().slice(0, 10) : "",
                                        },
                                      }))
                                    }
                                    dateFormat="dd-MM-yyyy"
                                    className="form-control"
                                    style={{ borderColor: THEME }}
                                    placeholderText="Select date"
                                  />
                                </td>
                              </tr>
                              <tr>
                                <td style={{ verticalAlign: "top", padding: "12px 14px" }}>
                                  <div className="fw-bold mb-1" style={{ color: "#8b1634", fontSize: "13px" }}>
                                    Review of control measures/interventions
                                  </div>
                                  <p style={{ fontSize: "12px", marginBottom: 0, color: "#4b5563" }}>
                                    • Discussion about working practices and working hours limits to reduce risk and
                                    identify controls to be implemented.
                                  </p>
                                </td>
                                <td style={{ verticalAlign: "top", padding: "12px 14px" }}>
                                  <textarea
                                    className="form-control"
                                    rows={3}
                                    value={editPayload.reviewOfControlMeasures?.agreedActions || ""}
                                    onChange={(e) =>
                                      setEditPayload((prev) => ({
                                        ...prev,
                                        reviewOfControlMeasures: {
                                          ...(prev.reviewOfControlMeasures || {}),
                                          agreedActions: e.target.value,
                                        },
                                      }))
                                    }
                                  />
                                </td>
                                <td style={{ verticalAlign: "top", padding: "12px 10px", textAlign: "center" }}>
                                  <DatePicker
                                    selected={
                                      editPayload.reviewOfControlMeasures?.reviewPeriodDate
                                        ? new Date(
                                            getDateInputValue(editPayload.reviewOfControlMeasures.reviewPeriodDate)
                                          )
                                        : null
                                    }
                                    onChange={(d) =>
                                      setEditPayload((prev) => ({
                                        ...prev,
                                        reviewOfControlMeasures: {
                                          ...(prev.reviewOfControlMeasures || {}),
                                          reviewPeriodDate: d ? d.toISOString().slice(0, 10) : "",
                                        },
                                      }))
                                    }
                                    dateFormat="dd-MM-yyyy"
                                    className="form-control"
                                    style={{ borderColor: THEME }}
                                    placeholderText="Select date"
                                  />
                                </td>
                              </tr>
                              <tr>
                                <td style={{ verticalAlign: "top", padding: "12px 14px" }}>
                                  <div className="fw-bold mb-1" style={{ color: "#8b1634", fontSize: "13px" }}>
                                    Medical review plans
                                  </div>
                                  <p style={{ fontSize: "12px", marginBottom: 0, color: "#4b5563" }}>
                                    • Discuss any OH recommendations relating to employee (returning to work after
                                    absence etc.).
                                  </p>
                                </td>
                                <td style={{ verticalAlign: "top", padding: "12px 14px" }}>
                                  <textarea
                                    className="form-control"
                                    rows={3}
                                    value={editPayload.medicalReviewPlans?.agreedActions || ""}
                                    onChange={(e) =>
                                      setEditPayload((prev) => ({
                                        ...prev,
                                        medicalReviewPlans: {
                                          ...(prev.medicalReviewPlans || {}),
                                          agreedActions: e.target.value,
                                        },
                                      }))
                                    }
                                  />
                                </td>
                                <td style={{ verticalAlign: "top", padding: "12px 10px", textAlign: "center" }}>
                                  <DatePicker
                                    selected={
                                      editPayload.medicalReviewPlans?.reviewPeriodDate
                                        ? new Date(getDateInputValue(editPayload.medicalReviewPlans.reviewPeriodDate))
                                        : null
                                    }
                                    onChange={(d) =>
                                      setEditPayload((prev) => ({
                                        ...prev,
                                        medicalReviewPlans: {
                                          ...(prev.medicalReviewPlans || {}),
                                          reviewPeriodDate: d ? d.toISOString().slice(0, 10) : "",
                                        },
                                      }))
                                    }
                                    dateFormat="dd-MM-yyyy"
                                    className="form-control"
                                    style={{ borderColor: THEME }}
                                    placeholderText="Select date"
                                  />
                                </td>
                              </tr>
                              <tr>
                                <td style={{ verticalAlign: "top", padding: "12px 14px" }}>
                                  <div className="fw-bold mb-1" style={{ color: "#8b1634", fontSize: "13px" }}>
                                    Any other comments
                                  </div>
                                  <p style={{ fontSize: "12px", marginBottom: 0, color: "#4b5563" }}>
                                    Note: include detail of any other occupational health management action plan.
                                  </p>
                                </td>
                                <td style={{ verticalAlign: "top", padding: "12px 14px" }}>
                                  <textarea
                                    className="form-control"
                                    rows={3}
                                    value={editPayload.otherComments?.agreedActions || ""}
                                    onChange={(e) =>
                                      setEditPayload((prev) => ({
                                        ...prev,
                                        otherComments: {
                                          ...(prev.otherComments || {}),
                                          agreedActions: e.target.value,
                                        },
                                      }))
                                    }
                                  />
                                </td>
                                <td style={{ verticalAlign: "top", padding: "12px 10px", textAlign: "center" }}>
                                  <DatePicker
                                    selected={
                                      editPayload.otherComments?.reviewPeriodDate
                                        ? new Date(getDateInputValue(editPayload.otherComments.reviewPeriodDate))
                                        : null
                                    }
                                    onChange={(d) =>
                                      setEditPayload((prev) => ({
                                        ...prev,
                                        otherComments: {
                                          ...(prev.otherComments || {}),
                                          reviewPeriodDate: d ? d.toISOString().slice(0, 10) : "",
                                        },
                                      }))
                                    }
                                    dateFormat="dd-MM-yyyy"
                                    className="form-control"
                                    style={{ borderColor: THEME }}
                                    placeholderText="Select date"
                                  />
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div className="mb-3 col-md-12">
                        <div className="fw-bold mb-1" style={{ color: "#8b1634" }}>
                          The above action plan has been discussed and agreed. Review dates will be as indicated.
                        </div>
                        <div className="row">
                          <div className="col-md-6">
                            <label className="form-label">Signature of employee</label>
                            <div
                              style={{
                                border: "1px solid #e2e8f0",
                                borderRadius: "8px",
                                padding: "8px",
                                background: "#fff5f5",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                minHeight: "120px",
                              }}
                            >
                              {editPayload.actionPlanAgreement?.employeeSignature ? (
                                <img
                                  src={
                                    String(editPayload.actionPlanAgreement.employeeSignature).startsWith("data:image")
                                      ? editPayload.actionPlanAgreement.employeeSignature
                                      : `data:image/png;base64,${editPayload.actionPlanAgreement.employeeSignature}`
                                  }
                                  alt="Employee Signature"
                                  style={{ maxWidth: "100%", maxHeight: "100px", objectFit: "contain" }}
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              ) : (
                                <span style={{ fontSize: "12px", color: "#a0aec0" }}>No signature available</span>
                              )}
                            </div>
                            <label className="form-label mt-2">Date</label>
                            <DatePicker
                              selected={
                                editPayload.actionPlanAgreement?.employeeDate
                                  ? new Date(getDateInputValue(editPayload.actionPlanAgreement.employeeDate))
                                  : null
                              }
                              onChange={(d) =>
                                setEditPayload((prev) => ({
                                  ...prev,
                                  actionPlanAgreement: {
                                    ...(prev.actionPlanAgreement || {}),
                                    employeeDate: d ? d.toISOString().slice(0, 10) : "",
                                  },
                                }))
                              }
                              dateFormat="dd-MM-yyyy"
                              className="form-control"
                              style={{ borderColor: THEME }}
                              placeholderText="Select date"
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Signature of person completing fatigue management plan</label>
                            <div
                              style={{
                                border: "1px solid #e2e8f0",
                                borderRadius: "8px",
                                padding: "8px",
                                background: "#fff5f5",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                minHeight: "120px",
                              }}
                            >
                              {editPayload.actionPlanAgreement?.managerSignature ? (
                                <img
                                  src={
                                    String(editPayload.actionPlanAgreement.managerSignature).startsWith("data:image")
                                      ? editPayload.actionPlanAgreement.managerSignature
                                      : `data:image/png;base64,${editPayload.actionPlanAgreement.managerSignature}`
                                  }
                                  alt="Manager Signature"
                                  style={{ maxWidth: "100%", maxHeight: "100px", objectFit: "contain" }}
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              ) : (
                                <span style={{ fontSize: "12px", color: "#a0aec0" }}>No signature available</span>
                              )}
                            </div>
                            <label className="form-label mt-2">Date</label>
                            <DatePicker
                              selected={
                                editPayload.actionPlanAgreement?.managerDate
                                  ? new Date(getDateInputValue(editPayload.actionPlanAgreement.managerDate))
                                  : null
                              }
                              onChange={(d) =>
                                setEditPayload((prev) => ({
                                  ...prev,
                                  actionPlanAgreement: {
                                    ...(prev.actionPlanAgreement || {}),
                                    managerDate: d ? d.toISOString().slice(0, 10) : "",
                                  },
                                }))
                              }
                              dateFormat="dd-MM-yyyy"
                              className="form-control"
                              style={{ borderColor: THEME }}
                              placeholderText="Select date"
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                  {editForm && editForm.level === "General" && (
                    <>
                      <div className="mb-2 col-md-12">
                        <div style={{ fontWeight: 600, color: "#8b1634", marginBottom: "6px" }}>Exceedance Details</div>
                      </div>
                    </>
                  )}
                  {editForm && editForm.level !== "Plan" && (
                    <>
                      <div className="mb-3 col-md-4">
                        <label className="form-label">Job Location</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editPayload.jobLocation || ""}
                          onChange={(e) =>
                            setEditPayload((prev) => ({ ...prev, jobLocation: e.target.value }))
                          }
                        />
                      </div>
                      <div className="mb-3 col-md-4">
                        <label className="form-label">Job No</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editPayload.jobNo || ""}
                          onChange={(e) => setEditPayload((prev) => ({ ...prev, jobNo: e.target.value }))}
                        />
                      </div>
                      <div className="mb-3 col-md-4">
                        <label className="form-label">Operator Name</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editPayload.operatorName || ""}
                          onChange={(e) =>
                            setEditPayload((prev) => ({ ...prev, operatorName: e.target.value }))
                          }
                        />
                      </div>
                      <div className="mb-3 col-md-4">
                        <label className="form-label">Date</label>
                        <DatePicker
                          selected={
                            getDateInputValue(editPayload.date)
                              ? new Date(getDateInputValue(editPayload.date))
                              : null
                          }
                          onChange={(d) =>
                            setEditPayload((prev) => ({
                              ...prev,
                              date: d ? d.toISOString().slice(0, 10) : "",
                            }))
                          }
                          dateFormat="dd-MM-yyyy"
                          className="form-control"
                          style={{ borderColor: THEME }}
                          placeholderText="Select date"
                        />
                      </div>
                      <div className="mb-3 col-md-6">
                        <label className="form-label">Client Name</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editPayload.clientName || ""}
                          onChange={(e) =>
                            setEditPayload((prev) => ({ ...prev, clientName: e.target.value }))
                          }
                        />
                      </div>
                    </>
                  )}

                  {editForm && editForm.level === "General" && editPayload.exceedanceTypes && (
                    <>
                      <div className="mb-2 col-md-12" style={{ marginTop: "4px" }}>
                        <div style={{ fontWeight: 600, color: "#8b1634", marginBottom: "4px" }}>Exceedance Type</div>
                      </div>
                      <div className="mb-3 col-md-12">
                        {Object.entries(editPayload.exceedanceTypes).map(([key, val]) => {
                          const current = String(val).toLowerCase();
                          const checked = current === "yes" || current === "true";
                          return (
                            <div key={key} className="form-check" style={{ marginBottom: "6px" }}>
                              <input
                                className="form-check-input theme-checkbox"
                                type="checkbox"
                                id={`ext-${key}`}
                                checked={checked}
                                onChange={(e) =>
                                  setEditPayload((prev) => ({
                                    ...prev,
                                    exceedanceTypes: {
                                      ...prev.exceedanceTypes,
                                      [key]: e.target.checked ? "yes" : "no",
                                    },
                                  }))
                                }
                                style={{ accentColor: THEME }}
                              />
                              <label className="form-check-label" htmlFor={`ext-${key}`} style={{ marginLeft: "8px" }}>
                                {key}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                      <div className="mb-2 col-md-12">
                        <div style={{ fontWeight: 600, color: "#8b1634", marginBottom: "4px" }}>Proposed Exceedance</div>
                      </div>
                    </>
                  )}

                  {editForm && editForm.level !== "Level 1" && editForm.level !== "Plan" && (
                    <>
                      <div className="mb-3 col-md-3">
                        <label className="form-label">Proposed From</label>
                        {editForm.level === "General" || editForm.level === "Level 2" ? (
                          <input
                            type="time"
                            className="form-control"
                            value={toHHmm(editPayload.proposedExceedanceFrom)}
                            onChange={(e) => setEditPayload((prev) => ({ ...prev, proposedExceedanceFrom: e.target.value }))}
                          />
                        ) : (
                          <DatePicker
                            selected={getDateInputValue(editPayload.proposedExceedanceFrom) ? new Date(getDateInputValue(editPayload.proposedExceedanceFrom)) : null}
                            onChange={(d) => setEditPayload((prev) => ({ ...prev, proposedExceedanceFrom: d ? d.toISOString().slice(0, 10) : "" }))}
                            dateFormat="dd-MM-yyyy"
                            className="form-control"
                            style={{ borderColor: THEME }}
                            placeholderText="Select date"
                          />
                        )}
                      </div>
                      <div className="mb-3 col-md-3">
                        <label className="form-label">Proposed To</label>
                        {editForm.level === "General" || editForm.level === "Level 2" ? (
                          <input
                            type="time"
                            className="form-control"
                            value={toHHmm(editPayload.proposedExceedanceTo)}
                            onChange={(e) => setEditPayload((prev) => ({ ...prev, proposedExceedanceTo: e.target.value }))}
                          />
                        ) : (
                          <DatePicker
                            selected={getDateInputValue(editPayload.proposedExceedanceTo) ? new Date(getDateInputValue(editPayload.proposedExceedanceTo)) : null}
                            onChange={(d) => setEditPayload((prev) => ({ ...prev, proposedExceedanceTo: d ? d.toISOString().slice(0, 10) : "" }))}
                            dateFormat="dd-MM-yyyy"
                            className="form-control"
                            style={{ borderColor: THEME }}
                            placeholderText="Select date"
                          />
                        )}
                      </div>
                      <div className="mb-3 col-md-3">
                        <label className="form-label">Proposed Hours</label>
                        <input
                          type="number"
                          className="form-control"
                          value={editPayload.proposedExceedanceHours ?? ""}
                          onChange={(e) =>
                            setEditPayload((prev) => ({
                              ...prev,
                              proposedExceedanceHours: e.target.value === "" ? null : Number(e.target.value),
                            }))
                          }
                        />
                      </div>
                      <div className="mb-3 col-md-3">
                        <label className="form-label">Proposed Minutes</label>
                        <input
                          type="number"
                          className="form-control"
                          value={editPayload.proposedExceedanceMinutes ?? ""}
                          onChange={(e) =>
                            setEditPayload((prev) => ({
                              ...prev,
                              proposedExceedanceMinutes: e.target.value === "" ? null : Number(e.target.value),
                            }))
                          }
                        />
                      </div>

                      {editForm.level === "General" && (
                        <div className="mb-2 col-md-12">
                          <div style={{ fontWeight: 600, color: "#8b1634", marginBottom: "4px" }}>Actual Exceedance</div>
                        </div>
                      )}

                      <div className="mb-3 col-md-3">
                        <label className="form-label">Actual From</label>
                        {editForm.level === "General" || editForm.level === "Level 2" ? (
                          <input
                            type="time"
                            className="form-control"
                            value={toHHmm(editPayload.actualExceedanceFrom)}
                            onChange={(e) => setEditPayload((prev) => ({ ...prev, actualExceedanceFrom: e.target.value }))}
                          />
                        ) : (
                          <DatePicker
                            selected={getDateInputValue(editPayload.actualExceedanceFrom) ? new Date(getDateInputValue(editPayload.actualExceedanceFrom)) : null}
                            onChange={(d) => setEditPayload((prev) => ({ ...prev, actualExceedanceFrom: d ? d.toISOString().slice(0, 10) : "" }))}
                            dateFormat="dd-MM-yyyy"
                            className="form-control"
                            style={{ borderColor: THEME }}
                            placeholderText="Select date"
                          />
                        )}
                      </div>
                      <div className="mb-3 col-md-3">
                        <label className="form-label">Actual To</label>
                        {editForm.level === "General" || editForm.level === "Level 2" ? (
                          <input
                            type="time"
                            className="form-control"
                            value={toHHmm(editPayload.actualExceedanceTo)}
                            onChange={(e) => setEditPayload((prev) => ({ ...prev, actualExceedanceTo: e.target.value }))}
                          />
                        ) : (
                          <DatePicker
                            selected={getDateInputValue(editPayload.actualExceedanceTo) ? new Date(getDateInputValue(editPayload.actualExceedanceTo)) : null}
                            onChange={(d) => setEditPayload((prev) => ({ ...prev, actualExceedanceTo: d ? d.toISOString().slice(0, 10) : "" }))}
                            dateFormat="dd-MM-yyyy"
                            className="form-control"
                            style={{ borderColor: THEME }}
                            placeholderText="Select date"
                          />
                        )}
                      </div>
                      <div className="mb-3 col-md-3">
                        <label className="form-label">Actual Hours</label>
                        <input
                          type="number"
                          className="form-control"
                          value={editPayload.actualExceedanceHours ?? ""}
                          onChange={(e) =>
                            setEditPayload((prev) => ({
                              ...prev,
                              actualExceedanceHours: e.target.value === "" ? null : Number(e.target.value),
                            }))
                          }
                        />
                      </div>
                      <div className="mb-3 col-md-3">
                        <label className="form-label">Actual Minutes</label>
                        <input
                          type="number"
                          className="form-control"
                          value={editPayload.actualExceedanceMinutes ?? ""}
                          onChange={(e) =>
                            setEditPayload((prev) => ({
                              ...prev,
                              actualExceedanceMinutes: e.target.value === "" ? null : Number(e.target.value),
                            }))
                          }
                        />
                      </div>
                    </>
                  )}

                  {editForm && editForm.level !== "Level 1" && editForm.level !== "Plan" && (
                    <div className="mb-3 col-md-12">
                      <label className="form-label">Reason for Exceedance / Comments</label>
                      <textarea
                        className="form-control"
                        rows={3}
                        value={editPayload.reasonForExceedanceComments || ""}
                        onChange={(e) =>
                          setEditPayload((prev) => ({
                            ...prev,
                            reasonForExceedanceComments: e.target.value,
                          }))
                        }
                      />
                    </div>
                  )}
                  {editForm && editForm.level === "Level 2" && (
                    <div className="mb-3 col-md-12">
                      <label className="form-label">Risk Mitigation Details</label>
                      <textarea
                        className="form-control"
                        rows={3}
                        value={editPayload.riskMitigationDetails || ""}
                        onChange={(e) =>
                          setEditPayload((prev) => ({
                            ...prev,
                            riskMitigationDetails: e.target.value,
                          }))
                        }
                      />
                    </div>
                  )}
                  {editForm && editForm.level !== "Level 1" && editForm.level !== "Plan" && (
                    <div className="mb-3 col-md-12">
                      <label className="form-label">Revised FRI Scores Following Mitigation</label>
                      <textarea
                        className="form-control"
                        rows={3}
                        value={editPayload.revisedFRIScoresFollowingMitigation || ""}
                        onChange={(e) =>
                          setEditPayload((prev) => ({
                            ...prev,
                            revisedFRIScoresFollowingMitigation: e.target.value,
                          }))
                        }
                      />
                    </div>
                  )}

                  {editForm && editForm.level === "Level 1" && editPayload.alertness && typeof editPayload.alertness === "object" && (
                    <div className="mb-3 col-md-12">
                      <div className="fw-bold mb-2" style={{ color: "#8b1634" }}>
                        Alertness
                      </div>
                      {Object.entries(editPayload.alertness).map(([question, answer]) => (
                        <div key={question} className="mb-3">
                          <label className="form-label" style={{ fontWeight: 500 }}>
                            {question}
                          </label>
                          <textarea
                            className="form-control"
                            rows={2}
                            value={answer || ""}
                            onChange={(e) =>
                              setEditPayload((prev) => ({
                                ...prev,
                                alertness: {
                                  ...prev.alertness,
                                  [question]: e.target.value,
                                },
                              }))
                            }
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {editForm && editForm.level === "Level 1" && Array.isArray(editPayload.howAlertOptions) && editPayload.howAlertOptions.length > 0 && (
                    <div className="mb-3 col-md-12">
                      <div className="fw-bold mb-2" style={{ color: "#8b1634" }}>
                        How alert are you feeling? <span style={{ fontWeight: 400 }}>(Select one)</span>
                      </div>
                      <div className="row">
                        {editPayload.howAlertOptions.map((option, index) => (
                          <div key={option} className="mb-2 col-md-6">
                            <div className="form-check">
                              <input
                                className="form-check-input theme-checkbox"
                                type="radio"
                                name="howAlertAreYouFeeling"
                                id={`alert-${index}`}
                                checked={String(editPayload.howAlertAreYouFeeling || "").trim() === option}
                                onChange={() =>
                                  setEditPayload((prev) => ({
                                    ...prev,
                                    howAlertAreYouFeeling: option,
                                  }))
                                }
                                style={{ accentColor: THEME }}
                              />
                              <label className="form-check-label" htmlFor={`alert-${index}`} style={{ marginLeft: "6px" }}>
                                {option}
                              </label>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {editForm && editForm.level === "Level 1" && editPayload.assessmentConsideration && typeof editPayload.assessmentConsideration === "object" && (
                    <div className="mb-3 col-md-12">
                      <div className="fw-bold mb-2" style={{ color: "#8b1634" }}>
                        Assessment consideration
                      </div>
                      {Object.entries(editPayload.assessmentConsideration).map(([question, answer]) => (
                        <div key={question} className="mb-3">
                          <label className="form-label" style={{ fontWeight: 500 }}>
                            {question}
                          </label>
                          <textarea
                            className="form-control"
                            rows={3}
                            value={answer || ""}
                            onChange={(e) =>
                              setEditPayload((prev) => ({
                                ...prev,
                                assessmentConsideration: {
                                  ...prev.assessmentConsideration,
                                  [question]: e.target.value,
                                },
                              }))
                            }
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {editForm && editForm.level === "Level 1" && (
                    <div className="mb-3 col-md-12">
                      <div className="fw-bold mb-2" style={{ color: "#8b1634" }}>
                        Overall Risk Rating is assessed as: delete as applicable
                      </div>
                      <div className="d-flex" style={{ gap: "24px" }}>
                        {["High", "Medium"].map((opt) => (
                          <div key={opt} className="form-check">
                            <input
                              className="form-check-input theme-checkbox"
                              type="radio"
                              name="overallRiskRating"
                              id={`orr-${opt}`}
                              checked={String(editPayload.overallRiskRating || "").trim() === opt}
                              onChange={() =>
                                setEditPayload((prev) => ({
                                  ...prev,
                                  overallRiskRating: opt,
                                }))
                              }
                              style={{ accentColor: THEME }}
                            />
                            <label className="form-check-label" htmlFor={`orr-${opt}`} style={{ marginLeft: "6px" }}>
                              {opt}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {editForm && editForm.level === "Level 1" && (
                    <div className="mb-3 col-md-12">
                      <label className="form-label">Additional Comments and Risk Mitigation Details</label>
                      <textarea
                        className="form-control"
                        rows={3}
                        value={editPayload.additionalCommentsAndRiskMitigation || ""}
                        onChange={(e) =>
                          setEditPayload((prev) => ({
                            ...prev,
                            additionalCommentsAndRiskMitigation: e.target.value,
                          }))
                        }
                      />
                    </div>
                  )}

                  {editPayload.exceedanceApprovedBy && (
                    <div className="mb-3 col-md-12">
                      {editForm && editForm.level === "Level 1" && (
                        <div className="fw-bold mb-2" style={{ color: "#8b1634" }}>
                          Exceedance Approved By Line Manager or On-Call Manager
                        </div>
                      )}
                      <div className="row">
                        <div className="mb-3 col-md-6">
                          <label className="form-label">Name</label>
                          <input
                            type="text"
                            className="form-control"
                            value={editPayload.exceedanceApprovedBy.name || ""}
                            onChange={(e) =>
                              setEditPayload((prev) => ({
                                ...prev,
                                exceedanceApprovedBy: {
                                  ...prev.exceedanceApprovedBy,
                                  name: e.target.value,
                                },
                              }))
                            }
                          />
                        </div>
                        <div className="mb-3 col-md-6">
                          <label className="form-label">Position</label>
                          <input
                            type="text"
                            className="form-control"
                            value={editPayload.exceedanceApprovedBy.position || ""}
                            onChange={(e) =>
                              setEditPayload((prev) => ({
                                ...prev,
                                exceedanceApprovedBy: {
                                  ...prev.exceedanceApprovedBy,
                                  position: e.target.value,
                                },
                              }))
                            }
                          />
                        </div>
                        <div className="mb-3 col-md-6">
                          <label className="form-label">Date</label>
                          <DatePicker
                            selected={editPayload.exceedanceApprovedBy.date ? new Date(getDateInputValue(editPayload.exceedanceApprovedBy.date)) : null}
                            onChange={(d) =>
                              setEditPayload((prev) => ({
                                ...prev,
                                exceedanceApprovedBy: {
                                  ...prev.exceedanceApprovedBy,
                                  date: d ? d.toISOString().slice(0, 10) : "",
                                },
                              }))
                            }
                            dateFormat="dd-MM-yyyy"
                            className="form-control"
                            style={{ borderColor: THEME }}
                            placeholderText="Select date"
                          />
                        </div>
                        <div className="mb-3 col-md-6">
                          <label className="form-label">Time</label>
                          <input
                            type="time"
                            className="form-control"
                            value={editPayload.exceedanceApprovedBy.time || ""}
                            onChange={(e) =>
                              setEditPayload((prev) => ({
                                ...prev,
                                exceedanceApprovedBy: {
                                  ...prev.exceedanceApprovedBy,
                                  time: e.target.value,
                                },
                              }))
                            }
                          />
                        </div>
                        {editPayload.exceedanceApprovedBy.sign && (
                          <div className="mb-3 col-md-12">
                            <label className="form-label">Sign</label>
                            <div
                              style={{
                                border: "1px solid #e2e8f0",
                                borderRadius: "8px",
                                padding: "8px",
                                background: "#fff5f5",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                minHeight: "120px",
                              }}
                            >
                              <img
                                src={
                                  String(editPayload.exceedanceApprovedBy.sign).startsWith("data:image")
                                    ? editPayload.exceedanceApprovedBy.sign
                                    : `data:image/png;base64,${editPayload.exceedanceApprovedBy.sign}`
                                }
                                alt="Signature"
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "100px",
                                  objectFit: "contain",
                                }}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {editPayload.ItemsAssessingProposedExceedance && (
                    <div className="mb-3 col-md-12">
                      <div className="fw-bold mb-2" style={{ color: "#8b1634" }}>
                        {editForm && editForm.level === "Level 2"
                          ? "Items to Consider When Assessing Proposed Exceedance"
                          : "Items Assessing Proposed Exceedance"}
                      </div>
                      {Object.entries(editPayload.ItemsAssessingProposedExceedance).map(([key, val]) => {
                        const current = String(val).toLowerCase();
                        const isYesNo = current === "yes" || current === "no";
                        if (editForm && editForm.level === "General") {
                          if (key.toLowerCase().includes("shift/hours worked prior to exceedance")) {
                            return (
                              <div key={key} className="mb-3">
                                <label className="form-label">{key}</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={val || ""}
                                  onChange={(e) =>
                                    setEditPayload((prev) => ({
                                      ...prev,
                                      ItemsAssessingProposedExceedance: {
                                        ...prev.ItemsAssessingProposedExceedance,
                                        [key]: e.target.value,
                                      },
                                    }))
                                  }
                                />
                              </div>
                            );
                          }
                          if (key.toLowerCase().includes("date of last rest period")) {
                            return (
                              <div key={key} className="mb-3">
                                <label className="form-label">{key}</label>
                                <DatePicker
                                  selected={getDateInputValue(val) ? new Date(getDateInputValue(val)) : null}
                                  onChange={(d) =>
                                    setEditPayload((prev) => ({
                                      ...prev,
                                      ItemsAssessingProposedExceedance: {
                                        ...prev.ItemsAssessingProposedExceedance,
                                        [key]: d ? d.toISOString().slice(0, 10) : "",
                                      },
                                    }))
                                  }
                                  dateFormat="dd-MM-yyyy"
                                  className="form-control"
                                  style={{ borderColor: THEME }}
                                  placeholderText="Select date"
                                />
                              </div>
                            );
                          }
                        }
                        if (editForm && editForm.level === "Level 2" && isYesNo) {
                          return (
                            <div key={key} className="d-flex align-items-center mb-2" style={{ gap: "12px" }}>
                              <div style={{ flex: 1 }}>{key}</div>
                              <div className="form-check form-check-inline">
                                <input
                                  className="form-check-input theme-checkbox"
                                  type="radio"
                                  name={`iap-${key}`}
                                  id={`iap-${key}-yes`}
                                  checked={current === "yes"}
                                  onChange={() =>
                                    setEditPayload((prev) => ({
                                      ...prev,
                                      ItemsAssessingProposedExceedance: {
                                        ...prev.ItemsAssessingProposedExceedance,
                                        [key]: "yes",
                                      },
                                    }))
                                  }
                                  style={{ accentColor: THEME }}
                                />
                                <label className="form-check-label" htmlFor={`iap-${key}-yes`}>
                                  Yes
                                </label>
                              </div>
                              <div className="form-check form-check-inline">
                                <input
                                  className="form-check-input theme-checkbox"
                                  type="radio"
                                  name={`iap-${key}`}
                                  id={`iap-${key}-no`}
                                  checked={current === "no"}
                                  onChange={() =>
                                    setEditPayload((prev) => ({
                                      ...prev,
                                      ItemsAssessingProposedExceedance: {
                                        ...prev.ItemsAssessingProposedExceedance,
                                        [key]: "no",
                                      },
                                    }))
                                  }
                                  style={{ accentColor: THEME }}
                                />
                                <label className="form-check-label" htmlFor={`iap-${key}-no`}>
                                  No
                                </label>
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div key={key} className="d-flex align-items-center mb-2" style={{ gap: "12px" }}>
                            <div className="form-check" style={{ flex: 1 }}>
                              <input
                                className="form-check-input theme-checkbox"
                                type="checkbox"
                                id={`iap-${key}`}
                                checked={current === "yes"}
                                onChange={(e) =>
                                  setEditPayload((prev) => ({
                                    ...prev,
                                    ItemsAssessingProposedExceedance: {
                                      ...prev.ItemsAssessingProposedExceedance,
                                      [key]: e.target.checked ? "yes" : "no",
                                    },
                                  }))
                                }
                                style={{ accentColor: THEME }}
                              />
                              <label className="form-check-label" htmlFor={`iap-${key}`} style={{ marginLeft: "8px" }}>
                                {key}
                              </label>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {editPayload.fatigueManagementPlanRequired && (
                    <div className="mb-3 col-md-12">
                      <div className="fw-bold mb-2" style={{ color: "#8b1634" }}>
                        Fatigue Management Plan Required
                      </div>
                      <div className="mb-2">
                        <span style={{ marginRight: "12px" }}>Required?</span>
                        <div className="form-check form-check-inline">
                          <input
                            className="form-check-input theme-checkbox"
                            type="radio"
                            name="fmpr-required"
                            id="fmpr-required-yes"
                            checked={editPayload.fatigueManagementPlanRequired.required === true}
                            onChange={() =>
                              setEditPayload((prev) => ({
                                ...prev,
                                fatigueManagementPlanRequired: {
                                  ...prev.fatigueManagementPlanRequired,
                                  required: true,
                                },
                              }))
                            }
                            style={{ accentColor: THEME }}
                          />
                          <label className="form-check-label" htmlFor="fmpr-required-yes">
                            Yes
                          </label>
                        </div>
                        <div className="form-check form-check-inline">
                          <input
                            className="form-check-input theme-checkbox"
                            type="radio"
                            name="fmpr-required"
                            id="fmpr-required-no"
                            checked={editPayload.fatigueManagementPlanRequired.required === false}
                            onChange={() =>
                              setEditPayload((prev) => ({
                                ...prev,
                                fatigueManagementPlanRequired: {
                                  ...prev.fatigueManagementPlanRequired,
                                  required: false,
                                },
                              }))
                            }
                            style={{ accentColor: THEME }}
                          />
                          <label className="form-check-label" htmlFor="fmpr-required-no">
                            No
                          </label>
                        </div>
                      </div>
                      {editPayload.fatigueManagementPlanRequired.required === true && (
                        <div className="row">
                          {Object.entries(editPayload.fatigueManagementPlanRequired)
                            .filter(([key]) => key !== "required")
                            .map(([key, val]) => {
                              const lowerKey = key.toLowerCase();
                              const isDate = lowerKey.includes("date");
                              const isTime = lowerKey.includes("time");
                              return (
                                <div key={key} className="mb-3 col-md-4">
                                  <label className="form-label">{key}</label>
                                  <input
                                    type={isDate ? "date" : isTime ? "time" : "text"}
                                    className="form-control"
                                    value={
                                      isDate
                                        ? getDateInputValue(val)
                                        : val || ""
                                    }
                                    onChange={(e) =>
                                      setEditPayload((prev) => ({
                                        ...prev,
                                        fatigueManagementPlanRequired: {
                                          ...prev.fatigueManagementPlanRequired,
                                          [key]: e.target.value,
                                        },
                                      }))
                                    }
                                    style={isDate ? { borderColor: THEME } : undefined}
                                  />
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </div>
                  )}

                  {editForm && editForm.level === "Level 2" && editPayload.calculatedFriScoreExceeds && typeof editPayload.calculatedFriScoreExceeds === "object" && (
                    <div className="mb-3 col-md-12">
                      <div className="fw-bold mb-2" style={{ color: "#8b1634" }}>
                        Does the calculated FRI score exceed any of the following:
                      </div>
                      {Object.entries(editPayload.calculatedFriScoreExceeds).map(([key, val]) => {
                        const current = String(val).toLowerCase();
                        return (
                          <div key={key} className="d-flex align-items-center mb-2" style={{ gap: "12px" }}>
                            <div style={{ flex: 1 }}>{key}</div>
                            <div className="form-check form-check-inline">
                              <input
                                className="form-check-input theme-checkbox"
                                type="radio"
                                name={`fri-${key}`}
                                id={`fri-${key}-yes`}
                                checked={current === "yes"}
                                onChange={() =>
                                  setEditPayload((prev) => ({
                                    ...prev,
                                    calculatedFriScoreExceeds: {
                                      ...prev.calculatedFriScoreExceeds,
                                      [key]: "yes",
                                    },
                                  }))
                                }
                                style={{ accentColor: THEME }}
                              />
                              <label className="form-check-label" htmlFor={`fri-${key}-yes`}>
                                Yes
                              </label>
                            </div>
                            <div className="form-check form-check-inline">
                              <input
                                className="form-check-input theme-checkbox"
                                type="radio"
                                name={`fri-${key}`}
                                id={`fri-${key}-no`}
                                checked={current === "no"}
                                onChange={() =>
                                  setEditPayload((prev) => ({
                                    ...prev,
                                    calculatedFriScoreExceeds: {
                                      ...prev.calculatedFriScoreExceeds,
                                      [key]: "no",
                                    },
                                  }))
                                }
                                style={{ accentColor: THEME }}
                              />
                              <label className="form-check-label" htmlFor={`fri-${key}-no`}>
                                No
                              </label>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {editForm && editForm.level === "Level 2" && (
                    <>
                      <div className="mb-3 col-md-6">
                        <label className="form-label">Shift/hours worked prior to exceedance</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editPayload.shiftHoursPriorToExceedance || ""}
                          onChange={(e) =>
                            setEditPayload((prev) => ({
                              ...prev,
                              shiftHoursPriorToExceedance: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div className="mb-3 col-md-6">
                        <label className="form-label">Date of last rest period</label>
                        <DatePicker
                          selected={getDateInputValue(editPayload.dateOfLastRestPeriod) ? new Date(getDateInputValue(editPayload.dateOfLastRestPeriod)) : null}
                          onChange={(d) => setEditPayload((prev) => ({ ...prev, dateOfLastRestPeriod: d ? d.toISOString().slice(0, 10) : "" }))}
                          dateFormat="dd-MM-yyyy"
                          className="form-control"
                          style={{ borderColor: THEME }}
                          placeholderText="Select date"
                        />
                      </div>
                      <div className="mb-3 col-md-3">
                        <label className="form-label">Next shift Date</label>
                        <DatePicker
                          selected={getDateInputValue(editPayload.nextShiftDate) ? new Date(getDateInputValue(editPayload.nextShiftDate)) : null}
                          onChange={(d) => setEditPayload((prev) => ({ ...prev, nextShiftDate: d ? d.toISOString().slice(0, 10) : "" }))}
                          dateFormat="dd-MM-yyyy"
                          className="form-control"
                          style={{ borderColor: THEME }}
                          placeholderText="Select date"
                        />
                      </div>
                      <div className="mb-3 col-md-3">
                        <label className="form-label">Next shift Time</label>
                        <input
                          type="time"
                          className="form-control"
                          value={editPayload.nextShiftTime || ""}
                          onChange={(e) =>
                            setEditPayload((prev) => ({
                              ...prev,
                              nextShiftTime: e.target.value,
                            }))
                          }
                        />
                      </div>
                    </>
                  )}

                  {editPayload.fatigueManagementPlanReviewRequired && (
                    <div className="mb-3 col-md-12">
                      <div className="fw-bold mb-2" style={{ color: "#8b1634" }}>
                        Fatigue Management Plan Review Required
                      </div>
                      <div className="mb-2">
                        <span style={{ marginRight: "12px" }}>Required?</span>
                        <div className="form-check form-check-inline">
                          <input
                            className="form-check-input theme-checkbox"
                            type="radio"
                            name="fmprr-required"
                            id="fmprr-required-yes"
                            checked={editPayload.fatigueManagementPlanReviewRequired.required === true}
                            onChange={() =>
                              setEditPayload((prev) => ({
                                ...prev,
                                fatigueManagementPlanReviewRequired: {
                                  ...prev.fatigueManagementPlanReviewRequired,
                                  required: true,
                                },
                              }))
                            }
                            style={{ accentColor: THEME }}
                          />
                          <label className="form-check-label" htmlFor="fmprr-required-yes">
                            Yes
                          </label>
                        </div>
                        <div className="form-check form-check-inline">
                          <input
                            className="form-check-input theme-checkbox"
                            type="radio"
                            name="fmprr-required"
                            id="fmprr-required-no"
                            checked={editPayload.fatigueManagementPlanReviewRequired.required === false}
                            onChange={() =>
                              setEditPayload((prev) => ({
                                ...prev,
                                fatigueManagementPlanReviewRequired: {
                                  ...prev.fatigueManagementPlanReviewRequired,
                                  required: false,
                                },
                              }))
                            }
                            style={{ accentColor: THEME }}
                          />
                          <label className="form-check-label" htmlFor="fmprr-required-no">
                            No
                          </label>
                        </div>
                      </div>
                      {editPayload.fatigueManagementPlanReviewRequired.required === true && (
                        <div className="row">
                          <div className="mb-3 col-md-4">
                            <label className="form-label">Position</label>
                            <input
                              type="text"
                              className="form-control"
                              value={editPayload.fatigueManagementPlanReviewRequired.position || ""}
                              onChange={(e) =>
                                setEditPayload((prev) => ({
                                  ...prev,
                                  fatigueManagementPlanReviewRequired: {
                                    ...prev.fatigueManagementPlanReviewRequired,
                                    position: e.target.value,
                                  },
                                }))
                              }
                            />
                          </div>
                          <div className="mb-3 col-md-4">
                            <label className="form-label">Date</label>
                            <DatePicker
                              selected={editPayload.fatigueManagementPlanReviewRequired.date ? new Date(getDateInputValue(editPayload.fatigueManagementPlanReviewRequired.date)) : null}
                              onChange={(d) =>
                                setEditPayload((prev) => ({
                                  ...prev,
                                  fatigueManagementPlanReviewRequired: {
                                    ...prev.fatigueManagementPlanReviewRequired,
                                    date: d ? d.toISOString().slice(0, 10) : "",
                                  },
                                }))
                              }
                              dateFormat="dd-MM-yyyy"
                              className="form-control"
                              style={{ borderColor: THEME }}
                              placeholderText="Select date"
                            />
                          </div>
                          <div className="mb-3 col-md-4">
                            <label className="form-label">Time</label>
                            <input
                              type="time"
                              className="form-control"
                              value={editPayload.fatigueManagementPlanReviewRequired.time || ""}
                              onChange={(e) =>
                                setEditPayload((prev) => ({
                                  ...prev,
                                  fatigueManagementPlanReviewRequired: {
                                    ...prev.fatigueManagementPlanReviewRequired,
                                    time: e.target.value,
                                  },
                                }))
                              }
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {editForm && editForm.level === "General" && (
                    <div className="mb-3 col-md-12">
                      <div className="fw-bold mb-1" style={{ color: "#8b1634" }}>
                        General exceedance - actions
                      </div>
                      <ul style={{ paddingLeft: "18px", marginBottom: 0, fontSize: "13px", color: "#4a5568" }}>
                        <li>Complete a fatigue assessment. Depending on the outcome of the assessment, the following may be required:</li>
                        <li>Identify and implement controls.</li>
                        <li>Document findings and actions in a fatigue management plan.</li>
                      </ul>
                    </div>
                  )}

                  {editForm && editForm.level === "Level 1" && (
                    <div className="mb-3 col-md-12">
                      <div className="fw-bold mb-1" style={{ color: "#8b1634" }}>
                        Guidance
                      </div>
                      <div style={{ fontSize: "13px", color: "#4a5568", marginBottom: "8px" }}>
                        <div style={{ fontWeight: 600, marginBottom: "4px" }}>Level 1 exceedance - actions</div>
                        <p style={{ marginBottom: "8px" }}>
                          Where the assessed fatigue risk is not identified as high:
                        </p>
                        <p style={{ marginBottom: "8px" }}>
                          the responsible person shall decide whether to allow further work to be undertaken with this
                          mitigation applied to affected staff:
                        </p>
                        <ul style={{ paddingLeft: "18px", marginBottom: "8px" }}>
                          <li>any safety critical duties shall be confined to a minimum or additional supervision shall be provided;</li>
                          <li>affected staff shall not be nominated for driving, lookout duties and/or IWA duties.</li>
                        </ul>
                        <p style={{ marginBottom: "8px" }}>
                          The functional head shall review this information and inform the responsible person whether they
                          are satisfied that the fatigue risk associated with the individual is being effectively managed.
                        </p>
                        <p style={{ marginBottom: "8px" }}>
                          Where the risk has been assessed as high, there are further actions which should be put in place
                          which are commensurate with the high risk of working over 60hrs. The individual should be relieved
                          from duty at the earliest opportunity, with the mandatory mitigations listed below in place until then.
                        </p>
                        <p style={{ marginBottom: "8px" }}>Mandatory mitigations include the individual being excluded from undertaking the following tasks until they have completed a minimum of 24hrs rest:</p>
                        <ul style={{ paddingLeft: "18px", marginBottom: "8px" }}>
                          <li>any safety critical tasks;</li>
                          <li>activities requiring the management of train/OTP/OTM movements;</li>
                          <li>all train exposure;</li>
                          <li>driving duties; and</li>
                          <li>plant operation.</li>
                        </ul>
                        <p style={{ marginBottom: 0 }}>
                          Where the Fatigue Assessment indicates medium risk, a suite of mitigations should be agreed between
                          the responsible person and the individual to manage the risk.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
                <button
                  type="button"
                  className="btn"
                  onClick={handleSaveEdit}
                  disabled={editSaving}
                  style={{
                    background: "#8b1634",
                    color: "#fff",
                    fontWeight: 600,
                    borderRadius: "8px",
                    padding: "8px 18px",
                    border: "none",
                  }}
                >
                  {editSaving ? "Saving..." : "Save changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showDeleteModal && deleteForm && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete Exceedance Form</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)} />
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this exceedance assessment?</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                <button type="button" className="btn btn-danger" onClick={handleConfirmDelete}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FatigueExceedanceFormsPage;
